import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import api from '../services/api';

const Settings = () => {
  const [bookingAllowedFrom, setBookingAllowedFrom] = useState('');
  const [bookingAllowedTill, setBookingAllowedTill] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      if (res.data.booking_allowed_till) {
        setBookingAllowedTill(res.data.booking_allowed_till);
      }
      if (res.data.booking_allowed_from) {
        setBookingAllowedFrom(res.data.booking_allowed_from);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    if (bookingAllowedFrom && bookingAllowedTill && bookingAllowedFrom > bookingAllowedTill) {
      setSnackbar({ open: true, message: 'Start date must be before end date.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      await api.put('/settings', {
        booking_allowed_from: bookingAllowedFrom || null,
        booking_allowed_till: bookingAllowedTill,
      });
      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save settings.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const today = format(new Date(), 'yyyy-MM-dd');

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#052e2b' }}>
        Settings
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 500 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#052e2b' }}>
          Booking Window
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set the date range within which users are allowed to make bookings.
        </Typography>

        <TextField
          label="Booking Start Date"
          type="date"
          fullWidth
          value={bookingAllowedFrom}
          onChange={(e) => setBookingAllowedFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Leave empty to allow booking from today"
          sx={{ mb: 2 }}
        />

        <TextField
          label="Booking End Date"
          type="date"
          fullWidth
          value={bookingAllowedTill}
          onChange={(e) => setBookingAllowedTill(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: today }}
          sx={{ mb: 3 }}
        />

        {(bookingAllowedFrom || bookingAllowedTill) && (
          <Typography variant="body2" sx={{ mb: 2, color: '#052e2b' }}>
            Users can book from{' '}
            <strong>
              {bookingAllowedFrom
                ? bookingAllowedFrom.split('-').reverse().join('-')
                : 'today'}
            </strong>{' '}
            to{' '}
            <strong>
              {bookingAllowedTill
                ? bookingAllowedTill.split('-').reverse().join('-')
                : 'no limit'}
            </strong>
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
